import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader } from 'lucide-react';
import { getMatchedGrants } from '../services/grantService';
import { UserAnswers, Grant } from '../types';
import { LeadCaptureForm } from './LeadCaptureForm';

export function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const answers = (location.state?.answers || {}) as UserAnswers;
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLeadCapture, setShowLeadCapture] = useState(!location.state?.leadData);

  useEffect(() => {
    if (!location.state?.answers) {
      navigate('/', { replace: true });
      return;
    }

    const leadData = location.state?.leadData;
    if (leadData) {
      console.log('Lead data available:', leadData);
    }

    async function fetchGrants() {
      try {
        const matchedGrants = await getMatchedGrants(answers);
        const currentDate = new Date();
        const activeGrants = matchedGrants.filter(grant => {
          const deadlineDate = new Date(grant.deadline);
          return deadlineDate > currentDate;
        });
        setGrants(activeGrants);
      } catch (err) {
        setError('Failed to fetch grants. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    fetchGrants();
  }, [answers, navigate, location.state]);

  if (showLeadCapture) {
    return (
      <div className="flex justify-center items-center min-h-[80vh]">
        <LeadCaptureForm 
          onSubmit={(data) => {
            setShowLeadCapture(false);
            // Here you would typically save the lead data
            console.log('Lead data:', data);
          }}
          onSkip={() => {
            setShowLeadCapture(false);
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-indigo-600 mb-4" />
        <p className="text-gray-600">Finding matching grants...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-red-600 hover:text-red-800"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Start New Search
      </button>

      <h2 className="text-3xl font-bold text-gray-900 mb-8">
        Matched Grants ({grants.length})
      </h2>

      {grants.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-900 mb-2">
            No matching grants found
          </h3>
          <p className="text-yellow-800">
            Try adjusting your search criteria to see more results.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {grants.map((grant) => (
            <div
              key={grant.id}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-200"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {grant.title}
                    </h3>
                    <span className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-full">
                      {grant.source}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-1">{grant.provider}</p>
                  <p className="text-gray-600 mb-4">{grant.description}</p>
                </div>
                <div className="text-right ml-6">
                  <div className="text-2xl font-bold text-indigo-600">
                    ${grant.amount.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    Match Score: {grant.matchScore}%
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Deadline</h4>
                  <p className="text-green-600 font-medium">
                    {new Date(grant.deadline).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Required Materials
                  </h4>
                  <ul className="list-disc list-inside text-gray-900">
                    {grant.requiredMaterials.map((material, index) => (
                      <li key={index} className="text-sm">{material}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <a
                  href={grant.applicationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Apply Now
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}